import { PortalBalancesTableProps } from '@/components/portal-modules/PortalBalancesTable';
import { convertedQtyFormatterConfig } from '@/config/config';
import { portalBalancesColumns } from '@/helpers/portalBalancesHelper';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import {
    ColumnDef,
    ExpandedState,
    filterFns,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useDateFormatter, useNumberFormatter } from 'react-aria';
import { useSso } from '../providers/SSOProvider';
import { getCurrency } from './useCurrency';
import { useDebouncedValue } from './useDebouncedValue';
import useLanguage from './useLanguage';
import { fuzzyGlobalFilter } from './useOpenPositionTable';
import { useTrueDateFormatter } from './useTrueDateFormatter';

interface UsePortalBalancesTableProps extends PortalBalancesTableProps {
    setInstrumentInfoContext: React.Dispatch<React.SetStateAction<any>>;
    handleOpenContextMenu: (row: any, x: number, y: number) => void;
}

const usePortalBalancesTable = (props: UsePortalBalancesTableProps) => {
    const { balances, baseCurrency, setInstrumentInfoContext, handleOpenContextMenu, onDeposit, onWithdraw } = props;

    const lang = useLanguage();
    const { foundEntity } = useSso();

    const currentAccount = useAppSelector(selectCurrentAccount);
    const formatQty = useNumberFormatter(convertedQtyFormatterConfig);
    const formatDate = useTrueDateFormatter();

    const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
    const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const dateFormatter = useDateFormatter({
        timeZone: 'UTC'
    });

    const isCryptoDepositWithdrawalEnabled = foundEntity?.attributes?.includes('DELIVERABLE');

    const columns: ColumnDef<any>[] = useMemo(() => {
        return portalBalancesColumns({
            lang,
            formatQty,
            formatDate,
            baseCurrency,
            totalInBaseCurrency: balances?.totalInBaseCurrency,
            isCryptoDepositWithdrawalEnabled,
            onDeposit,
            onWithdraw
        });
    }, [lang, balances, formatQty, setInstrumentInfoContext, handleOpenContextMenu]);

    const formattedPortalBalances: any[] = useMemo(() => {
        if (balances?.balances) {
            const returnValues = balances.balances.map((balance) => {
                const currency = getCurrency(balance?.currencyName || '');

                return {
                    ...balance,
                    subRows: balance.subRows
                        .filter((row) => row.currencyName !== 'Settlement Date')
                        .map((row) => ({
                            ...row,
                            tableCurrency: currency
                        })),
                    tableCurrency: currency
                };
            });

            returnValues.sort((a, b) => {
                if (a.tableCurrency < b.tableCurrency) return -1;
                if (a.tableCurrency > b.tableCurrency) return 1;
                return 0;
            });
            return returnValues;
        }

        return [];
    }, [balances, currentAccount]);

    const tableInstance = useReactTable({
        data: formattedPortalBalances,
        columns,
        state: { expanded: expandedRows, globalFilter, columnSizing, sorting },
        defaultColumn: { filterFn: filterFns.includesString },
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnId, filterValue) => {
            return fuzzyGlobalFilter(row, columnId, filterValue, dateFormatter);
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        onExpandedChange: setExpandedRows,
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        getExpandedRowModel: getExpandedRowModel(),
        getSubRows: (row: any) => row.subRows,
        enableRowSelection: true
    });

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 500);

    return { debouncedTableInstance, columnSizing, sorting, formattedPortalBalances, expandedRows, globalFilter };
};

export default usePortalBalancesTable;
